// LiveChat.js
import { useState, useEffect } from "react";
import {
  getActiveChats,
  getChatHistory,
  sendChatReply,
  exportChatsCsv,
} from "../../service/Mainservice";
import ExportCsvButton from "../ExportCsvButton";
import Loader from "../../Loader/Loader";
import Modal from "../Model/Model";
import ListControls from "../ListControls/ListControls";
import Pagination from "../Pagination/pagination";
import "./LiveChat.css";

// MUI Icons
import ChatIcon from "@mui/icons-material/Chat";
import SendIcon from "@mui/icons-material/Send";

const LiveChat = () => {
  const [loading, setLoading] = useState(true);
  const [chats, setChats] = useState([]);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
  });
  const [selectedChat, setSelectedChat] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const fetchActiveChats = async (page = 1) => {
    setLoading(true);
    try {
      const res = await getActiveChats({ page, limit: 10, sortBy, sortOrder });
      setChats(res.data || []);
      setPagination(res.pagination || { currentPage: 1, totalPages: 0 });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveChats(1);
  }, [sortBy, sortOrder]);

  const openChat = async (userId, userType) => {
    setSelectedChat({ userId, userType });
    setModalOpen(true);
    setHistoryLoading(true);
    try {
      const res = await getChatHistory({ userId, userType });
      setHistory(res.data?.messages || []);
      // Also store user profile
      setSelectedChat((prev) => ({ ...prev, profile: res.data?.userProfile }));
    } catch (err) {
      console.error(err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!replyMessage.trim()) return;
    try {
      await sendChatReply({
        userId: selectedChat.userId,
        userType: selectedChat.userType,
        message: replyMessage,
      });
      // Refresh history
      const res = await getChatHistory({
        userId: selectedChat.userId,
        userType: selectedChat.userType,
      });
      setHistory(res.data?.messages || []);
      setReplyMessage("");
    } catch (err) {
      alert(err.response?.data?.message || "Failed to send");
    }
  };

  return (
    <div className="livechat-page">
      <ListControls sortOptions={[{value:"createdAt",label:"Started date"},{value:"customer.fullName",label:"Customer"},{value:"status",label:"Status"},{value:"lastMessageAt",label:"Latest message"}]} sortBy={sortBy} sortOrder={sortOrder} onSortByChange={setSortBy} onSortOrderChange={setSortOrder} />
      <ExportCsvButton exportRequest={exportChatsCsv} filters={{ sortBy, sortOrder }} filename="live-chats.csv" />
      {loading ? (
        <Loader />
      ) : (
        <div className="chat-list">
          <h3>Active Conversations</h3>
          {chats.length === 0 ? (
            <p>No active chats.</p>
          ) : (
            <div className="chat-items">
              {chats.map((chat) => (
                <div
                  key={chat.userId}
                  className="chat-item"
                  onClick={() => openChat(chat.userId, chat.userType)}
                >
                  <div className="chat-avatar">
                    <img
                      src={chat.userProfile?.image || "/default-avatar.png"}
                      alt={chat.userProfile?.fullName}
                    />
                  </div>
                  <div className="chat-info">
                    <strong>{chat.userProfile?.fullName}</strong>
                    <span>{chat.userType}</span>
                    <p>{chat.lastMessage}</p>
                    <small>
                      {new Date(chat.lastMessageAt).toLocaleString()}
                    </small>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Pagination pagedata={pagination} onPageChange={fetchActiveChats} />
        </div>
      )}

      {/* Chat Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`Chat with ${selectedChat?.profile?.fullName || "User"}`}
        maxWidth="600px"
      >
        <div className="chat-window">
          <div className="chat-messages">
            {historyLoading ? (
              <Loader />
            ) : history.length === 0 ? (
              <p className="no-messages">No messages yet.</p>
            ) : (
              history.map((msg) => (
                <div
                  key={msg.id}
                  className={`message ${msg.sender === "agent" ? "agent" : "user"}`}
                >
                  <div className="message-bubble">
                    <p>{msg.message}</p>
                    <small>{new Date(msg.createdAt).toLocaleString()}</small>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="chat-input">
            <input
              type="text"
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              placeholder="Type your message..."
              onKeyPress={(e) => e.key === "Enter" && sendMessage()}
            />
            <button className="btn-primary" onClick={sendMessage}>
              <SendIcon fontSize="small" />
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LiveChat;
